<template>
    <div v-if="setStore.positionColumns.length > 0" class="mt-3 mb-5">
        <!-- Inline on desktop; behind a button on mobile, where the info column
             has no room for a slider. -->
        <ProductSetPositionSlider class="hidden md:block" />

        <button
            type="button"
            class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 md:hidden"
            @click="openSheet"
        >
            <span class="min-w-0 text-left">
                <span class="block font-semibold" v-text="$t('pages.product_detail_page.product_info.product_set.available_sizes_label')" />
                <span v-if="chosenSizesLabel" class="block truncate text-xs text-gray-500" v-text="chosenSizesLabel" />
            </span>

            <span class="flex shrink-0 items-center gap-3">
                <img v-if="mainImageUrl" :src="mainImageUrl" alt="" class="h-12 w-12 rounded-md object-cover" loading="lazy" />
                <ChevronRightIcon class="size-5" />
            </span>
        </button>

        <ProductSetPositionsSheet v-model="showPositions" :title="$t('pages.product_detail_page.product_info.product_set.available_sizes_label')">
            <ProductSetPositionSlider :framed="false" />

            <BigButton
                :label="$t('pages.product_detail_page.product_info.product_set.save_label')"
                class="mt-3 bg-order-buttons font-bold text-white"
                @click="saveAndClose"
            />
        </ProductSetPositionsSheet>
    </div>
</template>

<script setup lang="ts">
import BigButton from '@/components/buttons/BigButton.vue';
import ProductSetPositionSlider from '@/pages/product-page/partials/product-info/partials/set/ProductSetPositionSlider.vue';
import ProductSetPositionsSheet from '@/pages/product-page/partials/product-info/partials/set/ProductSetPositionsSheet.vue';
import { useProductSetStore } from '@/stores/products/ProductSetStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { ConfigurableProductFilterOptionObject, ConfigurableProductSetOptionObject } from '@/types/generated';
import { ImageTypeEnum, resolvedImageUrl } from '@/utils/imageUrl';
import { ChevronRightIcon } from '@lucide/vue';
import { computed, ref, watch } from 'vue';

const productStore = useProductStore();
const setStore = useProductSetStore();

const showPositions = ref(false);

// The selections as they were when the sheet opened; only the save button keeps changes.
const selectionsBeforeSheet = ref<Record<string, ConfigurableProductFilterOptionObject> | null>(null);

function openSheet(): void {
    selectionsBeforeSheet.value = { ...setStore.selectedFilterOptions };
    showPositions.value = true;
}

function saveAndClose(): void {
    selectionsBeforeSheet.value = null;
    showPositions.value = false;
}

// Closing any other way (X, overlay, Escape) throws the changes away.
watch(showPositions, (open: boolean) => {
    if (open || selectionsBeforeSheet.value === null) {
        return;
    }

    setStore.selectedFilterOptions = selectionsBeforeSheet.value;
    selectionsBeforeSheet.value = null;
});

// The chosen size per position, in position order, as the button's current-state line.
const chosenSizesLabel = computed<string>(() =>
    setStore.selectedOptions
        .map((option: ConfigurableProductSetOptionObject | undefined) => option?.label ?? option?.name ?? null)
        .filter(Boolean)
        .join(' · '),
);

// The image the gallery opens with; a set whose shots are not composed yet falls
// back to the first position's artwork.
const mainImageUrl = computed<string | null>(() => {
    const galleryImage = productStore.selectedProductImages[0];

    // A set gallery image carries a plain url; a variant image resolves through the image pipeline.
    if (galleryImage && 'caption' in galleryImage) {
        return galleryImage.url;
    }

    return (galleryImage ? resolvedImageUrl(galleryImage, ImageTypeEnum.Thumb) : null) ?? setStore.positionColumns[0]?.image?.url ?? null;
});
</script>
